import type { Analysis, AnalysisStatus } from '../models/card-model';
import type { DataService } from '../services/data.service';

function extractAnalysissArray(raw: unknown): unknown[] | null {
	if (!Array.isArray(raw)) {
		return null;
	}
	return raw;
}

function coerceAnalysisStatus(value: unknown): AnalysisStatus | undefined {
	if (value === 'DRAFT' || value === 'PUBLISHED') {
		return value;
	}
	return undefined;
}

/** WS rows map `analysisId` → {@link Analysis.id} (persisted key only). */
function coercePersistedAnalysisId(row: Record<string, unknown>): string {
	return typeof row.analysisId === 'string' ? row.analysisId.trim() : '';
}

function analysisTimestampKey(
	startTimestamp: number,
	endTimestamp: number
): string {
	return `analysis_${startTimestamp}_${endTimestamp}`;
}

export function hasPersistedAnalysisId(item: Pick<Analysis, 'id'>): boolean {
	return typeof item.id === 'string' && item.id.trim().length > 0;
}

/** Stable key for UI selection, trackBy, and in-memory list ops. */
export function analysisKey(item: Pick<Analysis, 'id' | 'runtimeId'>): string {
	if (hasPersistedAnalysisId(item)) {
		return item.id.trim();
	}
	const runtimeId =
		typeof item.runtimeId === 'string' ? item.runtimeId.trim() : '';
	return runtimeId;
}

export function analysisKeysMatch(
	a: Pick<Analysis, 'id' | 'runtimeId'>,
	b: Pick<Analysis, 'id' | 'runtimeId'>
): boolean {
	return analysisKey(a) === analysisKey(b);
}

export function analysisRangeKey(
	item: Pick<Analysis, 'startTimestamp' | 'endTimestamp'>
): string | null {
	if (item.startTimestamp > 0 && item.endTimestamp > 0) {
		return `${item.startTimestamp}:${item.endTimestamp}`;
	}
	return null;
}

function coerceAnalysisRuntimeId(
	row: Record<string, unknown>,
	startTimestamp: number,
	endTimestamp: number,
	persistedId: string
): string | undefined {
	if (persistedId || startTimestamp <= 0 || endTimestamp <= 0) {
		return undefined;
	}
	return analysisTimestampKey(startTimestamp, endTimestamp);
}

// fallow-ignore-next-line complexity
function coerceAnalysis(raw: unknown): Analysis | null {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
		return null;
	}
	const row = raw as Record<string, unknown>;
	const headline =
		typeof row.headline === 'string' ? row.headline.trim() : '';
	if (!headline) {
		return null;
	}
	const startTimestamp =
		typeof row.startTimestamp === 'number' ? row.startTimestamp : 0;
	const endTimestamp =
		typeof row.endTimestamp === 'number' ? row.endTimestamp : 0;
	const id = coercePersistedAnalysisId(row);
	const runtimeId = coerceAnalysisRuntimeId(
		row,
		startTimestamp,
		endTimestamp,
		id
	);
	if (!id && !runtimeId) {
		return null;
	}
	const content = typeof row.content === 'string' ? row.content : '';
	const label =
		typeof row.label === 'string' && row.label.trim()
			? row.label.trim()
			: undefined;
	const revisions =
		typeof row.revisions === 'number' && Number.isFinite(row.revisions)
			? row.revisions
			: undefined;
	const status = coerceAnalysisStatus(row.status);

	return {
		id,
		...(runtimeId ? { runtimeId } : {}),
		startTimestamp,
		endTimestamp,
		headline,
		content,
		...(label ? { label } : {}),
		...(status ? { status } : {}),
		...(revisions !== undefined ? { revisions } : {}),
	};
}

/** Incoming rows: drop rows without persisted id or runtime key, or without headline. */
function coerceAnalysesFromPayload(raw: readonly unknown[]): Analysis[] {
	const items: Analysis[] = [];
	for (let i = 0; i < raw.length; i++) {
		const item = coerceAnalysis(raw[i]);
		if (item) {
			items.push(item);
		}
	}
	return items.length > 0 ? normalizeAnalyses(items) : [];
}

/** Runtime analyses for an analysis card (from `data.analyses` on the card event). */
type AnalysesData = Analysis[];

/**
 * Build {@link AnalysesData} from a parsed card event (`messageType: data`, `cardType: analysis`).
 * Payload lives under `data.analyses` (same envelope as chart/table).
 */
function analysesDataFromCardEvent(event: {
	cardId: string;
	data?: unknown;
}): AnalysesData | null {
	if (!event.cardId) {
		return null;
	}

	if (
		event.data != null &&
		typeof event.data === 'object' &&
		!Array.isArray(event.data)
	) {
		const nested = extractAnalysissArray(
			(event.data as Record<string, unknown>)['analyses']
		);
		if (nested) {
			return coerceAnalysesFromPayload(nested);
		}
	}

	return null;
}

/**
 * Normalize and store {@link AnalysesData} on {@link DataService} (report grid + editor).
 * Completed payloads with `data.analyses` as an array (including empty / headline-less
 * rows) are persisted so canvas cards can leave the loading state.
 * Returns true only when {@link DataService.updateAnalysesData} accepted the write
 * (stale requestIds and empty overwrites without requestId are rejected).
 */
export function applyAnalysesDataToDataService(
	dataService: DataService,
	event: {
		cardId: string;
		data?: unknown;
		requestId?: string;
	}
): boolean {
	const analyses = analysesDataFromCardEvent(event);
	if (analyses === null) {
		return false;
	}
	return dataService.updateAnalysesData(
		event.cardId,
		analyses,
		event.requestId
	);
}

/** Normalize the editable headline; empty strings are valid draft/create state. */
function normalizeAnalysisHeadline(item: { headline?: string }): string {
	return typeof item.headline === 'string' ? item.headline.trim() : '';
}

/** Normalize one analysis item for UI (trimmed headline). */
function normalizeAnalysis(item: Analysis): Analysis {
	return {
		...item,
		headline: normalizeAnalysisHeadline(item),
	};
}

export function normalizeAnalyses(items: readonly Analysis[]): Analysis[] {
	return items.map(normalizeAnalysis);
}

/** True when an analysis item uses the same UTC reporting window (inclusive end). */
export function analysisMatchesEpochRange(
	item: Pick<Analysis, 'startTimestamp' | 'endTimestamp'>,
	startMs: number,
	endMs: number
): boolean {
	return item.startTimestamp === startMs && item.endTimestamp === endMs;
}

export function replaceAnalysis(
	analyses: Analysis[],
	itemId: string,
	patch: Partial<Analysis>
): Analysis[] {
	return analyses.map(item =>
		analysisKey(item) === itemId ? { ...item, ...patch } : item
	);
}

export function findAnalysisIndex(
	analyses: Analysis[],
	itemId: string
): number {
	return analyses.findIndex(item => analysisKey(item) === itemId);
}

export function removeAnalysis(
	analyses: Analysis[],
	itemId: string
): Analysis[] {
	return analyses.filter(item => analysisKey(item) !== itemId);
}

/** True when an analysis item is visible in report preview (`PUBLISHED`). */
function isPublishedAnalysis(item: Analysis): boolean {
	return item.status === 'PUBLISHED';
}

/** Whether an analysis reporting period overlaps a UTC epoch range (inclusive). */
export function analysisOverlapsEpochRange(
	item: Analysis,
	rangeStartMs: number,
	rangeEndMs: number
): boolean {
	return (
		item.startTimestamp <= rangeEndMs && item.endTimestamp >= rangeStartMs
	);
}

/** Draft periods in range that are hidden from report/card preview readers. */
export function countDraftAnalysesOverlappingRange(
	analyses: readonly Analysis[],
	rangeStartMs: number,
	rangeEndMs: number
): number {
	return analyses.filter(
		item =>
			!isPublishedAnalysis(item) &&
			analysisOverlapsEpochRange(item, rangeStartMs, rangeEndMs)
	).length;
}

/** Report preview: only published periods are shown to readers. */
export function publishedAnalysiss(items: readonly Analysis[]): Analysis[] {
	return items.filter(isPublishedAnalysis);
}
